import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSeo } from "@/hooks/useSeo";
import { getArticleBySlug, ARTICLES } from "@/data/brandBranding";
import { SITE } from "@/data/config";
import { Overline, Crumbs } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";

// Build a Table of Contents from the article body H2s.
const extractHeadings = (body) => {
  const lines = body.split("\n");
  const headings = [];
  for (const ln of lines) {
    const m = ln.match(/^##\s+(.+)$/);
    if (m) {
      const text = m[1].trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      headings.push({ text, id });
    }
  }
  return headings;
};

// Slugify H2s so ReactMarkdown-rendered headings match the ToC anchors.
const slugifyId = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

// Markdown component overrides — module-scope so React does not recreate on every render.
const MD_COMPONENTS = {
  h1: ({ children }) => (
    <h2 className="mt-16 text-3xl lg:text-4xl font-black tracking-tighter">{children}</h2>
  ),
  h2: ({ children }) => {
    const text = React.Children.toArray(children).join("");
    const id = slugifyId(text);
    return (
      <h2 id={id} className="mt-14 lg:mt-16 text-2xl lg:text-3xl font-black tracking-tighter leading-tight scroll-mt-24">
        {children}
      </h2>
    );
  },
  h3: ({ children }) => (
    <h3 className="mt-10 text-xl lg:text-2xl font-bold tracking-tight leading-snug text-white">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mt-5 text-white/85 text-base lg:text-lg leading-[1.75]">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mt-5 space-y-2 pl-6 list-disc marker:text-[#FFC600] text-white/85 text-base lg:text-lg leading-[1.75]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-5 space-y-2 pl-6 list-decimal marker:text-[#FFC600] text-white/85 text-base lg:text-lg leading-[1.75]">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1 leading-[1.75]">{children}</li>,
  strong: ({ children }) => <strong className="text-white font-bold">{children}</strong>,
  em: ({ children }) => <em className="text-white italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="mt-8 border-l-2 border-[#FFC600] pl-6 text-white/85 italic text-lg leading-relaxed">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-[#FFC600] underline underline-offset-4 hover:text-white">
      {children}
    </a>
  ),
  hr: () => <hr className="my-12 border-white/15" />,
};

const NavLine = ({ label, article, testId }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600]">
      {label}
    </span>
    <Link
      to={`/brand-branding/${article.slug}`}
      data-testid={testId}
      className="text-sm lg:text-base font-semibold text-white hover:text-[#FFC600] transition-colors leading-snug"
    >
      {article.title}
    </Link>
  </div>
);

const BrandBrandingArticle = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  const previous = article?.previousSlug ? getArticleBySlug(article.previousSlug) : null;
  const continueWith = article?.continueWithSlug ? getArticleBySlug(article.continueWithSlug) : null;
  const related = useMemo(
    () => (article?.related || []).map((s) => getArticleBySlug(s)).filter(Boolean),
    [article]
  );
  const headings = useMemo(() => (article ? extractHeadings(article.body) : []), [article]);

  const absUrl = article ? `${SITE.url}/brand-branding/${article.slug}` : SITE.url;

  useSeo(
    article
      ? {
          title: `${article.metaTitle} · Kostaja`,
          description: article.metaDescription,
          canonical: absUrl,
          ogImage: `${SITE.url}/og/kostaja-og-default.jpg?v=1`,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.metaDescription,
              url: absUrl,
              articleSection: article.cluster,
              inLanguage: "en-CA",
              isPartOf: {
                "@type": "CreativeWorkSeries",
                name: "Brand & Branding",
                url: `${SITE.url}/brand-branding`,
              },
              author: { "@type": "Organization", name: "Kostaja Ltd.", url: SITE.url },
              publisher: {
                "@type": "Organization",
                name: "Kostaja Ltd.",
                url: SITE.url,
                logo: {
                  "@type": "ImageObject",
                  url: `${SITE.url}/kostaja-512.png`,
                },
              },
              mainEntityOfPage: absUrl,
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
                { "@type": "ListItem", position: 2, name: "Brand & Branding", item: `${SITE.url}/brand-branding` },
                { "@type": "ListItem", position: 3, name: article.title, item: absUrl },
              ],
            },
            ...(article.faqs && article.faqs.length
              ? [
                  {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: article.faqs.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  },
                ]
              : []),
          ],
        }
      : {}
  );

  if (!article) return <Navigate to="/brand-branding" replace />;

  return (
    <main data-testid="brand-branding-article" className="bg-black text-white">
      {/* TOP: COLLECTION NAV MODULE */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-10 lg:pt-14 pb-8">
          <Crumbs
            items={[
              { to: "/", label: "Home" },
              { to: "/brand-branding", label: "Brand & Branding" },
              { label: article.title },
            ]}
          />
          <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-bold">
            <Link to="/brand-branding" className="text-[#FFC600] hover:text-white transition-colors">
              Brand & Branding
            </Link>
            <span className="text-white/50">/</span>
            <span className="text-white/75">{article.cluster}</span>
            <span className="text-white/50">/</span>
            <span className="text-white/75">Essay {String(article.order).padStart(2, "0")}</span>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-6 pb-16 lg:pb-20">
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-[76px] font-black tracking-tighter leading-[0.95] max-w-5xl">
            {article.title}
          </h1>
          <p className="mt-6 max-w-3xl text-white/70 text-lg lg:text-xl leading-relaxed">
            {article.excerpt}
          </p>
        </div>
      </section>

      {/* BODY + TOC */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Sticky ToC */}
          {headings.length > 3 ? (
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24">
                <Overline color="yellow" className="mb-4">On this page</Overline>
                <ul className="space-y-2 border-l border-white/15 pl-4">
                  {headings.map((h) => (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        data-testid={`toc-${h.id}`}
                        className="text-sm text-white/75 hover:text-[#FFC600] leading-snug block py-1"
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          ) : null}

          {/* Body */}
          <article className={headings.length > 3 ? "lg:col-span-9 order-1 lg:order-2 max-w-3xl" : "lg:col-span-12 max-w-3xl mx-auto"}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {article.body}
            </ReactMarkdown>
          </article>
        </div>
      </section>

      {/* BOTTOM: CONTINUE + RELATED */}
      <section data-testid="collection-continuation" className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <Overline color="yellow">Keep going</Overline>
          <h2 className="mt-3 text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
            The most logical next concept.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {previous ? (
              <Link
                to={`/brand-branding/${previous.slug}`}
                data-testid="nav-previous"
                className="group block p-6 lg:p-8 border border-white/15 hover:border-[#FFC600] transition-colors"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/70 mb-3">
                  ← Previously
                </div>
                <div className="text-xl lg:text-2xl font-black tracking-tighter leading-tight text-white group-hover:text-[#FFC600] transition-colors">
                  {previous.title}
                </div>
                <p className="mt-3 text-sm text-white/75">{previous.excerpt}</p>
              </Link>
            ) : (
              <div className="p-6 lg:p-8 border border-white/10 opacity-60">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45 mb-3">
                  Start of the collection
                </div>
                <div className="text-white/70 text-sm">
                  You are on the first essay. Continue on to build the full picture.
                </div>
              </div>
            )}
            {continueWith ? (
              <Link
                to={`/brand-branding/${continueWith.slug}`}
                data-testid="nav-continue"
                className="group block p-6 lg:p-8 border-2 border-[#FFC600] hover:bg-[#FFC600] hover:text-black transition-colors"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600] group-hover:text-black mb-3">
                  Continue with →
                </div>
                <div className="text-xl lg:text-2xl font-black tracking-tighter leading-tight">
                  {continueWith.title}
                </div>
                <p className="mt-3 text-sm text-white/70 group-hover:text-black/80">{continueWith.excerpt}</p>
              </Link>
            ) : article.nextComing ? (
              <div
                data-testid="nav-continue-coming"
                className="p-6 lg:p-8 border-2 border-[#FFC600]/50 bg-black"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600] mb-3">
                  Coming next
                </div>
                <div className="text-xl lg:text-2xl font-black tracking-tighter leading-tight text-white">
                  {article.nextComing.title}
                </div>
                <p className="mt-3 text-sm text-white/75">{article.nextComing.note}</p>
                <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/70">
                  Opens the {article.nextComing.cluster} cluster
                </div>
              </div>
            ) : (
              <div className="p-6 lg:p-8 border border-white/10">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600] mb-3">
                  End of the collection
                </div>
                <div className="text-white/85 text-sm">
                  You have reached the last essay. Head back to the hub to review the full path or jump to any adjacent topic.
                </div>
                <Link to="/brand-branding" className="mt-4 inline-block text-sm font-semibold text-[#FFC600] underline underline-offset-4">
                  Back to the collection
                </Link>
              </div>
            )}
          </div>

          {related.length > 0 ? (
            <div className="mt-16">
              <Overline color="yellow">Adjacent topics</Overline>
              <h3 className="mt-3 text-2xl lg:text-3xl font-black tracking-tighter">
                Useful essays next to this one.
              </h3>
              <ul className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={`/brand-branding/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="block h-full p-5 border border-white/15 hover:border-[#FFC600] transition-colors"
                    >
                      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/70 mb-2">
                        {r.cluster}
                      </div>
                      <div className="text-base lg:text-lg font-bold tracking-tight leading-snug text-white">
                        {r.title}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      {/* Restrained service CTA */}
      <CTABlock variant="blog" />
    </main>
  );
};

export default BrandBrandingArticle;
